import { createContext, useEffect, useMemo, useState } from 'react';
import { loadNikkeIconCatalog } from '../api/nikkeIconApi.js';
import { buildNikkeIconLookup } from '../domain/nikkeIcons.js';

export const NikkeIconContext = createContext({ lookup: new Map(), loading: true });

let sharedRows = null;
let sharedPromise = null;
const CATALOG_RETRY_DELAYS_MS = [2000, 6000, 15000];

export function NikkeIconProvider({ children }) {
  const [rows, setRows] = useState(sharedRows || []);
  const [loading, setLoading] = useState(!sharedRows);

  useEffect(() => {
    let active = true;
    let retryTimer = null;
    if (sharedRows) return () => { active = false; };
    // The provider mounts once for the whole app, so a single failed request used to leave every
    // catalog-only icon on its text fallback until a reload. Retry a few times with backoff, and
    // again whenever the browser reports it is back online.
    const load = (attempt) => {
      if (!sharedPromise) sharedPromise = loadNikkeIconCatalog();
      sharedPromise.then((nextRows) => {
        sharedRows = nextRows;
        if (active) { setRows(nextRows); setLoading(false); }
      }).catch(() => {
        sharedPromise = null;
        if (!active) return;
        if (attempt < CATALOG_RETRY_DELAYS_MS.length) {
          retryTimer = setTimeout(() => load(attempt + 1), CATALOG_RETRY_DELAYS_MS[attempt]);
        } else {
          setLoading(false);
        }
      });
    };
    const onOnline = () => { if (!sharedRows) { clearTimeout(retryTimer); load(0); } };
    load(0);
    window.addEventListener('online', onOnline);
    return () => {
      active = false;
      clearTimeout(retryTimer);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  const value = useMemo(() => ({ lookup: buildNikkeIconLookup(rows), loading }), [loading, rows]);
  return <NikkeIconContext.Provider value={value}>{children}</NikkeIconContext.Provider>;
}
